import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Loader2, ArrowLeft, Download, BookOpen, Copy, Check,
  Cloud, CloudOff, FolderOpen, FilePlus, FileText, Printer, FileType, LayoutTemplate,
  FileSignature, Presentation, Layers,
} from 'lucide-react';

/**
 * Top header bar for the Sermon Prep tool — navigation, autosave chip,
 * drafts/new/copy buttons, and export-format dropdown.
 */
const SermonPrepHeader = ({
  language,
  navigate,
  saveStatus,
  savedAgoLabel,
  openDraftsDialog,
  openTemplatesDialog,
  startNewSermon,
  copyToClipboard,
  copied,
  onExportTxt,
  onExportMarkdown,
  onExportPdf,
  onExportManuscript,
  onStartPreaching,
  onSendToSlidecon,
  sendingSlides,
}) => {
  const { t } = useLanguage();
  return (
    <header className="gradient-primary border-b border-white/20 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50 shadow-lg">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-white/30"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-white" />
            <h1 className="text-lg sm:text-xl font-bold text-white">
              {t('sermon.title')}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Autosave status chip */}
          <div
            className="hidden sm:flex items-center gap-1.5 text-xs text-white/85 px-2 py-1 rounded-full bg-white/10 border border-white/20"
            data-testid="sermon-save-status"
          >
            {saveStatus === 'saving' && (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>{t('sermon.saving')}</span>
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <Cloud className="w-3 h-3" />
                <span>
                  {t('sermon.saved')}
                  {savedAgoLabel && ` · ${savedAgoLabel}`}
                </span>
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <CloudOff className="w-3 h-3" />
                <span>{t('sermon.offline')}</span>
              </>
            )}
            {saveStatus === 'idle' && (
              <>
                <CloudOff className="w-3 h-3 opacity-70" />
                <span>
                  {t('sermon.unsavedAddTopic')}
                </span>
              </>
            )}
          </div>

          <Button
            onClick={openTemplatesDialog}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-white/30"
            title={t('sermon.outlineTemplates')}
            data-testid="sermon-open-templates-btn"
          >
            <LayoutTemplate className="w-4 h-4" />
          </Button>
          <Button
            onClick={openDraftsDialog}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-white/30"
            title={t('sermon.myDrafts')}
            data-testid="sermon-open-drafts-btn"
          >
            <FolderOpen className="w-4 h-4" />
          </Button>
          <Button
            onClick={startNewSermon}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-white/30"
            title={t('sermon.newSermon')}
            data-testid="sermon-new-btn"
          >
            <FilePlus className="w-4 h-4" />
          </Button>

          <Button
            onClick={onStartPreaching}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-yellow-200/60 bg-white/5"
            title={t('sermon.preachingMode')}
            data-testid="sermon-preach-btn"
          >
            <Presentation className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline text-xs font-semibold">
              {t('sermon.preach')}
            </span>
          </Button>

          <Button
            onClick={onSendToSlidecon}
            disabled={sendingSlides}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-cyan-200/60 bg-white/5"
            title={t('sermon.createSlidesWith')}
            data-testid="sermon-slidecon-btn"
          >
            {sendingSlides ? (
              <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
            ) : (
              <Layers className="w-4 h-4 sm:mr-1" />
            )}
            <span className="hidden sm:inline text-xs font-semibold">
              {t('sermon.slides')}
            </span>
          </Button>

          <Button
            onClick={copyToClipboard}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 border border-white/30"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 border border-white/30"
                data-testid="sermon-export-trigger"
              >
                <Download className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="sermon-export-menu">
              <DropdownMenuItem onClick={onExportTxt} data-testid="sermon-export-txt-btn">
                <FileType className="w-4 h-4 mr-2" />
                {t('sermon.exportTxt')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportMarkdown} data-testid="sermon-export-md-btn">
                <FileText className="w-4 h-4 mr-2" />
                Markdown (.md)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportPdf} data-testid="sermon-export-pdf-btn">
                <Printer className="w-4 h-4 mr-2" />
                {t('sermon.exportPdf')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportManuscript} data-testid="sermon-export-manuscript-btn">
                <FileSignature className="w-4 h-4 mr-2" />
                {t('sermon.exportManuscript')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default SermonPrepHeader;
